import React, { useState, useEffect, type ReactNode } from 'react';

import {
  type AuthResponse,
  AuthService,
  type GenericErrorResponse,
  type User
} from '../services/authService';
import type { AuthContextType } from './AuthContext.types';
import { AuthContext } from './AuthContext.context';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState<boolean>(true);
  const [profileNeedsNames, setProfileNeedsNames] = useState<boolean>(false);

  const isUser = (value: unknown): value is User => {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Partial<User>;

    return (
      typeof candidate.id === 'string' &&
      typeof candidate.email === 'string' &&
      typeof candidate.can_invite === 'boolean' &&
      typeof candidate.is_organisation_admin === 'boolean'
    );
  };

  const extractUser = (response: AuthResponse<User>): User | null => {
    const candidate = (response.user ?? response.data) as unknown;
    return isUser(candidate) ? candidate : null;
  };

  const refreshAuthStatus = async (): Promise<void> => {
    try {
      const result = await AuthService.getCurrentUser();

      const resolvedUser = extractUser(result);

      if (!resolvedUser) {
        throw new Error('User data missing from current user response');
      }

      setUser(resolvedUser);
      setIsAuthenticated(true);
      setProfileNeedsNames(!resolvedUser.first_name || !resolvedUser.last_name);
      // Keep shouldRedirect as true for already authenticated users
      setShouldRedirect(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Auth check failed';
      console.error('Auth check failed:', message);
      setUser(null);
      setIsAuthenticated(false);
      setProfileNeedsNames(false);
      setShouldRedirect(true);
    } finally {
      // A delay to prevent too frequent state updates
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  };

  // Check auth status when the app loads
  useEffect(() => {
    refreshAuthStatus();

    // Listen for auth events
    const handleAuthLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  const logout = async (): Promise<boolean> => {
    try {
      const result = await AuthService.logout();

      setUser(null);
      setIsAuthenticated(false);
      setProfileNeedsNames(false);

      // Dispatch logout event for any legacy code still listening
      window.dispatchEvent(new CustomEvent('auth:logout'));

      return result.success;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      console.error('Logout failed:', message);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      // Optionally, you could add a delay here as well
      // await new Promise(resolve => setTimeout(resolve, 100));
    }
  };

  const login = async (email: string, password: string, remember: boolean = false): Promise<AuthResponse<User> | GenericErrorResponse> => {
    try {
      const result = await AuthService.login(email, password, remember);
      console.log('[AuthContext] Login result:', result);

      const resolvedUser = extractUser(result);

      if (!resolvedUser) {
        throw new Error('Login response missing user data');
      }

      setUser(resolvedUser);
      setIsAuthenticated(true);
      setProfileNeedsNames(!resolvedUser.first_name || !resolvedUser.last_name);
      // Prevent immediate redirect
      setShouldRedirect(false);

      // Allow redirect after showing success message
      setTimeout(() => {setShouldRedirect(true);}, 2000);

      // Always return the result object so the Login component can handle errors
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      console.error('[AuthContext] Login failed:', message);
      setProfileNeedsNames(false);
      return { success: false, error: message };
    }
  };

  const completeProfile = async (firstName: string, lastName: string): Promise<boolean> => {
    try {
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();

      if (!trimmedFirst || !trimmedLast) {
        throw new Error('First and last name are required');
      }

      const response = await AuthService.updateProfileNames(trimmedFirst, trimmedLast);

      const updatedUser = extractUser(response);

      if (!updatedUser) {
        throw new Error('Profile update response missing user data');
      }

      setUser(updatedUser);
      setProfileNeedsNames(false);
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      console.error('[AuthContext] completeProfile failed:', message);
      return false;
    }
  };

  // Values to expose through the context
  const value: AuthContextType = {
    isAuthenticated,
    user,
    shouldRedirect,
    refreshAuthStatus,
    logout,
    login,
    profileNeedsNames,
    completeProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};